import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentoAPI, guiasConfigAPI } from '../lib/api';
import { compressImage } from '../lib/compressImage';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
    FileText, 
    Truck, 
    Building, 
    Plus, 
    Trash2, 
    Loader2, 
    Search, 
    CheckCircle, 
    Upload, 
    FileImage, 
    X,
    User,
    AlertCircle,
    Camera,
    Image as ImageIcon
} from 'lucide-react';

// Función para normalizar RUT chileno (sin puntos, con guión)
const normalizarRut = (rut) => {
    if (!rut) return '';
    // Eliminar espacios, puntos y guiones
    let cleaned = rut.toString().trim().replace(/\./g, '').replace(/-/g, '').replace(/\s/g, '').toUpperCase();
    
    if (cleaned.length < 2) return cleaned;
    
    // Separar cuerpo y dígito verificador
    const cuerpo = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    
    return `${cuerpo}-${dv}`;
};

// Función para validar RUT chileno
const validarRut = (rut) => {
    if (!rut) return false;
    const cleaned = rut.replace(/\./g, '').replace(/-/g, '').replace(/\s/g, '').toUpperCase();
    
    if (cleaned.length < 2) return false;
    
    const cuerpo = cleaned.slice(0, -1);
    const dvIngresado = cleaned.slice(-1);
    
    if (!/^\d+$/.test(cuerpo)) return false;
    
    // Calcular dígito verificador
    let suma = 0;
    let multiplicador = 2;
    for (let i = cuerpo.length - 1; i >= 0; i--) {
        suma += parseInt(cuerpo[i]) * multiplicador;
        multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const resto = suma % 11;
    const dvCalculado = 11 - resto;
    
    let dvEsperado;
    if (dvCalculado === 11) dvEsperado = '0';
    else if (dvCalculado === 10) dvEsperado = 'K';
    else dvEsperado = dvCalculado.toString();
    
    return dvIngresado === dvEsperado;
};

export default function CrearGuiaPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [consultandoRut, setConsultandoRut] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);

    // Configuración
    const [tiposDocumento, setTiposDocumento] = useState([]);
    const [etiquetaSeccionEmpresa, setEtiquetaSeccionEmpresa] = useState('Datos empresa');
    const [aprobadores, setAprobadores] = useState([]);

    // Form state
    const [tipoDocumento, setTipoDocumento] = useState('');
    const [patenteTracto, setPatenteTracto] = useState('');
    const [patenteSemiremolque, setPatenteSemiremolque] = useState('');
    const [nombreConductor, setNombreConductor] = useState('');
    const [rutConductor, setRutConductor] = useState('');
    const [rutConductorValido, setRutConductorValido] = useState(false);
    const [rutEmpresa, setRutEmpresa] = useState('');
    const [rutEmpresaValido, setRutEmpresaValido] = useState(false);
    const [razonSocial, setRazonSocial] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [items, setItems] = useState([{ descripcion: '', cantidad: '', unidad: 'UN' }]);
    const [folioFisico, setFolioFisico] = useState('');
    const [documentoAdjunto, setDocumentoAdjunto] = useState(null);
    const [documentoAdjuntoUrl, setDocumentoAdjuntoUrl] = useState(null);
    const [imagenesCarga, setImagenesCarga] = useState([]);
    const [uploadingImagesCarga, setUploadingImagesCarga] = useState(false);
    const [aprobadorId, setAprobadorId] = useState('');
    const [esResiduoNoPeligroso, setEsResiduoNoPeligroso] = useState(false);
    const [documentoFMA002, setDocumentoFMA002] = useState(null);
    const [documentoFMA002Url, setDocumentoFMA002Url] = useState(null);
    const [uploadingFMA002, setUploadingFMA002] = useState(false);

    // Obtener nombre del tipo de documento seleccionado
    const getTipoDocumentoNombre = () => {
        const tipo = tiposDocumento.find(t => t.id === tipoDocumento);
        return tipo?.nombre || 'documento';
    };

    // Cargar configuración al montar
    useEffect(() => {
        loadConfiguracion();
        loadAprobadores();
    }, []);

    const loadAprobadores = async () => {
        try {
            const res = await guiasConfigAPI.getAprobadores();
            setAprobadores(res.data || []);
        } catch (err) {
            console.error('Error cargando aprobadores:', err);
            setAprobadores([]);
        }
    };

    const loadConfiguracion = async () => {
        try {
            setLoadingConfig(true);
            const res = await guiasConfigAPI.getConfiguracion();
            const config = res.data;
            
            const tiposActivos = (config.tipos_documento || []).filter(t => t.activo !== false);
            setTiposDocumento(tiposActivos);
            setEtiquetaSeccionEmpresa(config.etiqueta_seccion_empresa || 'Datos empresa');
            
            // Seleccionar primer tipo por defecto
            if (tiposActivos.length > 0 && !tipoDocumento) {
                setTipoDocumento(tiposActivos[0].id);
            }
        } catch (err) {
            console.error('Error cargando configuración:', err);
            // Valores por defecto si falla
            setTiposDocumento([
                { id: 'guia_despacho', nombre: 'Guía de despacho' },
                { id: 'factura', nombre: 'Factura' }
            ]);
            setTipoDocumento('guia_despacho');
        } finally {
            setLoadingConfig(false);
        }
    };

    // Manejar cambio de RUT conductor con normalización
    const handleRutConductorChange = (value) => {
        const normalizado = normalizarRut(value);
        setRutConductor(normalizado);
        setRutConductorValido(validarRut(normalizado));
    };

    // Manejar cambio de RUT empresa con normalización y consulta al SII
    const handleRutEmpresaChange = async (value) => {
        const normalizado = normalizarRut(value);
        setRutEmpresa(normalizado);
        
        const esValido = validarRut(normalizado);
        setRutEmpresaValido(esValido);
        
        // Si es válido y tiene al menos 8 caracteres, consultar SII
        if (esValido && normalizado.length >= 9) {
            await consultarRutSII(normalizado);
        } else {
            setRazonSocial('');
        }
    };

    // Consultar razón social en SII
    const consultarRutSII = async (rut) => {
        try {
            setConsultandoRut(true);
            const res = await guiasConfigAPI.consultarRut(rut);
            if (res.data.razon_social) {
                setRazonSocial(res.data.razon_social);
            } else {
                setRazonSocial('');
            }
        } catch (err) {
            console.error('Error consultando RUT:', err);
            setRazonSocial('');
        } finally {
            setConsultandoRut(false);
        }
    };

    // Manejar items
    const addItem = () => {
        setItems([...items, { descripcion: '', cantidad: '', unidad: 'UN' }]);
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    // Upload de archivo adjunto con compresión
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tipo
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Tipo de archivo no permitido. Use JPG, PNG, WebP o PDF');
            return;
        }

        // Validar tamaño (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('El archivo es muy grande. Máximo 10MB');
            return;
        }

        setUploadingFile(true);
        try {
            // Comprimir si es imagen
            const fileToUpload = await compressImage(file, 1920, 0.75);
            
            const formData = new FormData();
            formData.append('file', fileToUpload);
            const res = await documentoAPI.uploadAdjunto(formData);
            setDocumentoAdjuntoUrl(res.data.url);
            setDocumentoAdjunto(fileToUpload);
            toast.success('Documento adjuntado');
        } catch (err) {
            toast.error('Error al subir el documento');
        } finally {
            setUploadingFile(false);
        }
    };

    const removeAdjunto = () => {
        setDocumentoAdjunto(null);
        setDocumentoAdjuntoUrl(null);
    };

    // Upload de imágenes de carga con compresión
    const handleImagenesCargaUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        // Validar cantidad máxima
        if (imagenesCarga.length + files.length > 5) {
            toast.error('Máximo 5 imágenes de carga permitidas');
            return;
        }

        // Validar tipos
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
        if (invalidFiles.length > 0) {
            toast.error('Solo se permiten imágenes JPG, PNG o WebP');
            return;
        }

        setUploadingImagesCarga(true);
        try {
            const newImages = [];
            for (const file of files) {
                // Comprimir imagen
                const compressedFile = await compressImage(file, 1920, 0.7);
                
                const formData = new FormData();
                formData.append('file', compressedFile);
                const res = await documentoAPI.uploadAdjunto(formData);
                newImages.push({
                    file: compressedFile,
                    url: res.data.url,
                    name: file.name
                });
            }
            setImagenesCarga([...imagenesCarga, ...newImages]);
            toast.success(`${newImages.length} imagen(es) agregada(s)`);
        } catch (err) {
            toast.error('Error al subir las imágenes');
        } finally {
            setUploadingImagesCarga(false);
        }
    };

    const removeImagenCarga = (index) => {
        setImagenesCarga(imagenesCarga.filter((_, i) => i !== index));
    };

    // Upload de documento F-MA-002 con compresión
    const handleFMA002Upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Tipo de archivo no permitido. Use JPG, PNG, WebP o PDF');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('El archivo es muy grande. Máximo 10MB');
            return;
        }

        setUploadingFMA002(true);
        try {
            const fileToUpload = await compressImage(file, 1920, 0.75);
            
            const formData = new FormData();
            formData.append('file', fileToUpload);
            const res = await documentoAPI.uploadAdjunto(formData);
            setDocumentoFMA002Url(res.data.url);
            setDocumentoFMA002(fileToUpload);
            toast.success('Documento F-MA-002 adjuntado');
        } catch (err) {
            toast.error('Error al subir el documento F-MA-002');
        } finally {
            setUploadingFMA002(false);
        }
    };

    const removeFMA002 = () => {
        setDocumentoFMA002(null);
        setDocumentoFMA002Url(null);
    };

    // Enviar formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones
        if (!tipoDocumento) {
            toast.error('Seleccione un tipo de documento');
            return;
        }
        if (!aprobadorId) {
            toast.error('Seleccione un aprobador para la solicitud');
            return;
        }
        if (!folioFisico.trim()) {
            toast.error(`Ingrese el folio ${getTipoDocumentoNombre()} física`);
            return;
        }
        if (!documentoAdjuntoUrl) {
            toast.error('Debe adjuntar el documento/foto del documento físico');
            return;
        }
        if (!patenteTracto.trim()) {
            toast.error('Ingrese la patente del tracto');
            return;
        }
        if (!nombreConductor.trim()) {
            toast.error('Ingrese el nombre del conductor');
            return;
        }
        if (!rutConductor.trim() || !rutConductorValido) {
            toast.error('Ingrese un RUT de conductor válido');
            return;
        }
        if (!rutEmpresa.trim() || !rutEmpresaValido) {
            toast.error('Ingrese un RUT de empresa válido');
            return;
        }
        if (!razonSocial.trim()) {
            toast.error('Debe obtenerse la razón social de la empresa');
            return;
        }
        if (!descripcion.trim()) {
            toast.error('Ingrese las observaciones');
            return;
        }
        if (imagenesCarga.length === 0) {
            toast.error('Debe adjuntar al menos una imagen de la carga');
            return;
        }

        // Filtrar items vacíos
        const itemsValidos = items.filter(item => item.descripcion.trim());

        setLoading(true);
        try {
            const data = {
                tipo_documento: tipoDocumento,
                patente_tracto: patenteTracto.toUpperCase().trim(),
                patente_semiremolque: patenteSemiremolque ? patenteSemiremolque.toUpperCase().trim() : null,
                nombre_conductor: nombreConductor.trim(),
                rut_conductor: rutConductor,
                rut_empresa: rutEmpresa,
                razon_social: razonSocial.trim(),
                descripcion: descripcion.trim(),
                items: itemsValidos,
                folio_fisico: folioFisico.trim(),
                documento_adjunto_url: documentoAdjuntoUrl,
                imagenes_carga: imagenesCarga.map(img => img.url),
                aprobador_id: aprobadorId,
                es_residuo_no_peligroso: esResiduoNoPeligroso,
                documento_fma002_url: documentoFMA002Url,
            };

            await documentoAPI.create(data);
            toast.success('Guía creada correctamente');
            navigate('/guias/mis-guias');
        } catch (err) {
            console.error('Error creando guía:', err);
            toast.error(err.response?.data?.detail || 'Error al crear la guía');
        } finally {
            setLoading(false);
        }
    };

    if (loadingConfig) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[50vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <FileText className="h-6 w-6" />
                        Nueva Guía de Despacho
                    </h1>
                    <p className="text-muted-foreground">Complete los datos para crear una nueva guía</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Tipo de Documento */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                Tipo de Documento
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="tipo">Tipo *</Label>
                                    <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                                        <SelectTrigger data-testid="select-tipo-documento">
                                            <SelectValue placeholder="Seleccione tipo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tiposDocumento.map(tipo => (
                                                <SelectItem key={tipo.id} value={tipo.id}>
                                                    {tipo.nombre}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="folioFisico">Folio {getTipoDocumentoNombre()} física *</Label>
                                    <Input
                                        id="folioFisico"
                                        value={folioFisico}
                                        onChange={(e) => setFolioFisico(e.target.value)}
                                        placeholder="N° de folio impreso"
                                        required
                                        data-testid="input-folio-fisico"
                                    />
                                </div>
                            </div>

                            {/* Documento/Foto Adjunta - Obligatorio */}
                            <div className="space-y-2">
                                <Label>Documento/Foto Adjunta *</Label>
                                {documentoAdjunto ? (
                                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                                        <FileImage className="h-5 w-5 text-green-600" />
                                        <span className="flex-1 text-sm truncate">{documentoAdjunto.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {(documentoAdjunto.size / 1024).toFixed(0)} KB
                                        </span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={removeAdjunto}
                                            className="text-destructive"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="border-2 border-dashed border-primary/50 rounded-lg p-6 text-center bg-primary/5">
                                        <input
                                            type="file"
                                            id="adjunto"
                                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor="adjunto"
                                            className="cursor-pointer flex flex-col items-center gap-2"
                                        >
                                            {uploadingFile ? (
                                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                            ) : (
                                                <Upload className="h-8 w-8 text-primary" />
                                            )}
                                            <span className="text-sm font-medium text-primary">
                                                {uploadingFile ? 'Subiendo y comprimiendo...' : 'Adjuntar foto/documento del folio físico'}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                JPG, PNG, WebP o PDF (se comprime automáticamente)
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Datos del Vehículo */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Truck className="h-5 w-5" />
                                Datos del Vehículo
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="patenteTracto">PATENTE TRACTO *</Label>
                                    <Input
                                        id="patenteTracto"
                                        value={patenteTracto}
                                        onChange={(e) => setPatenteTracto(e.target.value.toUpperCase())}
                                        placeholder="Ej: ABCD12"
                                        className="uppercase"
                                        required
                                        data-testid="input-patente-tracto"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="patenteSemiremolque">Patente Semiremolque (opcional)</Label>
                                    <Input
                                        id="patenteSemiremolque"
                                        value={patenteSemiremolque}
                                        onChange={(e) => setPatenteSemiremolque(e.target.value.toUpperCase())}
                                        placeholder="Ej: WXYZ34"
                                        className="uppercase"
                                        data-testid="input-patente-semiremolque"
                                    />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nombreConductor">Nombre Conductor *</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="nombreConductor"
                                            value={nombreConductor}
                                            onChange={(e) => setNombreConductor(e.target.value)}
                                            placeholder="Nombre completo"
                                            className="pl-9"
                                            required
                                            data-testid="input-nombre-conductor"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="rutConductor">RUT Conductor *</Label>
                                    <div className="relative">
                                        <Input
                                            id="rutConductor"
                                            value={rutConductor}
                                            onChange={(e) => handleRutConductorChange(e.target.value)}
                                            placeholder="12345678-9"
                                            className={`pr-10 ${rutConductor && !rutConductorValido ? 'border-red-500' : ''}`}
                                            required
                                            data-testid="input-rut-conductor"
                                        />
                                        {rutConductor && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                {rutConductorValido ? (
                                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <AlertCircle className="h-4 w-4 text-red-500" />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {rutConductor && !rutConductorValido && (
                                        <p className="text-xs text-red-500">RUT inválido</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Datos Empresa (configurable) */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building className="h-5 w-5" />
                                {etiquetaSeccionEmpresa}
                            </CardTitle>
                            <CardDescription>
                                Ingrese el RUT para obtener automáticamente la razón social
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="rutEmpresa">RUT Empresa *</Label>
                                    <div className="relative">
                                        <Input
                                            id="rutEmpresa"
                                            value={rutEmpresa}
                                            onChange={(e) => handleRutEmpresaChange(e.target.value)}
                                            placeholder="77155297-8"
                                            className={`pr-10 ${rutEmpresa && !rutEmpresaValido ? 'border-red-500' : ''}`}
                                            required
                                            data-testid="input-rut-empresa"
                                        />
                                        {consultandoRut ? (
                                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                                        ) : rutEmpresa && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                {rutEmpresaValido ? (
                                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <AlertCircle className="h-4 w-4 text-red-500" />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {rutEmpresa && !rutEmpresaValido && (
                                        <p className="text-xs text-red-500">RUT inválido</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="razonSocial">Razón Social</Label>
                                    <Input
                                        id="razonSocial"
                                        value={razonSocial}
                                        onChange={(e) => setRazonSocial(e.target.value)}
                                        placeholder="Se obtiene automáticamente del SII"
                                        className="bg-muted"
                                        data-testid="input-razon-social"
                                    />
                                    {consultandoRut && (
                                        <p className="text-xs text-muted-foreground">Consultando SII...</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Items / Detalle */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Detalle de Carga</CardTitle>
                                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                                    <Plus className="h-4 w-4 mr-1" />
                                    Agregar Item
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {items.map((item, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <div className="flex-1">
                                        <Input
                                            value={item.descripcion}
                                            onChange={(e) => updateItem(index, 'descripcion', e.target.value)}
                                            placeholder="Descripción del producto"
                                            data-testid={`input-item-desc-${index}`}
                                        />
                                    </div>
                                    <div className="w-24">
                                        <Input
                                            type="number"
                                            value={item.cantidad}
                                            onChange={(e) => updateItem(index, 'cantidad', e.target.value)}
                                            placeholder="Cant."
                                            data-testid={`input-item-cant-${index}`}
                                        />
                                    </div>
                                    <div className="w-24">
                                        <Select
                                            value={item.unidad}
                                            onValueChange={(v) => updateItem(index, 'unidad', v)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="UN">UN</SelectItem>
                                                <SelectItem value="KG">KG</SelectItem>
                                                <SelectItem value="LT">LT</SelectItem>
                                                <SelectItem value="MT">MT</SelectItem>
                                                <SelectItem value="M3">M3</SelectItem>
                                                <SelectItem value="TON">TON</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {items.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeItem(index)}
                                            className="text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}

                            {/* Imágenes de la Carga - Obligatorio */}
                            <div className="pt-4 border-t space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="text-base font-semibold flex items-center gap-2">
                                        <Camera className="h-4 w-4" />
                                        Fotos de la Carga * 
                                        <span className="text-xs font-normal text-muted-foreground">
                                            ({imagenesCarga.length}/5)
                                        </span>
                                    </Label>
                                </div>

                                {/* Grid de imágenes */}
                                {imagenesCarga.length > 0 && (
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                        {imagenesCarga.map((img, index) => (
                                            <div key={index} className="relative group aspect-square bg-muted rounded-lg overflow-hidden border">
                                                <img
                                                    src={img.url}
                                                    alt={`Carga ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="destructive"
                                                    size="icon"
                                                    onClick={() => removeImagenCarga(index)}
                                                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                                                    {img.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Botón para agregar imágenes */}
                                {imagenesCarga.length < 5 && (
                                    <div className="border-2 border-dashed border-primary/50 rounded-lg p-4 text-center bg-primary/5">
                                        <input
                                            type="file"
                                            id="imagenesCarga"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            multiple
                                            onChange={handleImagenesCargaUpload}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor="imagenesCarga"
                                            className="cursor-pointer flex flex-col items-center gap-2"
                                        >
                                            {uploadingImagesCarga ? (
                                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                            ) : (
                                                <ImageIcon className="h-6 w-6 text-primary" />
                                            )}
                                            <span className="text-sm font-medium text-primary">
                                                {uploadingImagesCarga ? 'Comprimiendo y subiendo...' : 'Agregar fotos de la carga'}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                JPG, PNG o WebP (máx. 5 imágenes, se comprimen automáticamente)
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* F-MA-002 para residuos no peligrosos */}
                            <div className="pt-4 border-t space-y-3">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="esResiduoNoPeligroso"
                                        checked={esResiduoNoPeligroso}
                                        onChange={(e) => setEsResiduoNoPeligroso(e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <Label htmlFor="esResiduoNoPeligroso" className="cursor-pointer">
                                        ¿Es residuo no peligroso? (requiere F-MA-002)
                                    </Label>
                                </div>

                                {esResiduoNoPeligroso && (
                                    <div className="space-y-2 ml-7">
                                        <Label>Documento F-MA-002 (opcional)</Label>
                                        {documentoFMA002 ? (
                                            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                                                <FileImage className="h-5 w-5 text-blue-600" />
                                                <span className="flex-1 text-sm truncate">{documentoFMA002.name}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {(documentoFMA002.size / 1024).toFixed(0)} KB
                                                </span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={removeFMA002}
                                                    className="text-destructive"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center bg-blue-50/50 dark:bg-blue-900/10">
                                                <input
                                                    type="file"
                                                    id="fma002"
                                                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                                                    onChange={handleFMA002Upload}
                                                    className="hidden"
                                                />
                                                <label
                                                    htmlFor="fma002"
                                                    className="cursor-pointer flex flex-col items-center gap-2"
                                                >
                                                    {uploadingFMA002 ? (
                                                        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                                    ) : (
                                                        <Upload className="h-6 w-6 text-blue-500" />
                                                    )}
                                                    <span className="text-sm font-medium text-blue-600">
                                                        {uploadingFMA002 ? 'Subiendo...' : 'Adjuntar F-MA-002'}
                                                    </span>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Observaciones - Obligatorio */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Observaciones</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <Label htmlFor="descripcion">Observaciones *</Label>
                                <Textarea
                                    id="descripcion"
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                    placeholder="Ingrese observaciones sobre el despacho..."
                                    rows={3}
                                    required
                                    data-testid="input-descripcion"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Dirigir solicitud a - Al final, destacado */}
                    <Card className="border-2 border-primary/30 bg-primary/5">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Enviar Solicitud
                            </CardTitle>
                            <CardDescription>
                                Seleccione el aprobador que recibirá esta solicitud
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <Label htmlFor="aprobador">Dirigir solicitud a *</Label>
                                <Select value={aprobadorId} onValueChange={setAprobadorId}>
                                    <SelectTrigger data-testid="select-aprobador" className="bg-background">
                                        <SelectValue placeholder="Seleccione un aprobador" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {aprobadores.map(apr => (
                                            <SelectItem key={apr.id} value={apr.id}>
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{apr.nombre}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {apr.cargo ? `${apr.cargo} • ` : ''}{apr.email}
                                                    </span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Botones */}
                    <div className="flex justify-end gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/guias/mis-guias')}
                        >
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading} data-testid="submit-btn">
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Creando...
                                </>
                            ) : (
                                <>
                                    <FileText className="h-4 w-4 mr-2" />
                                    Crear Guía
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
}
